import { useState } from 'react';
import SoundAvatar from '../Common/SoundAvatar.jsx';
import Waveform from '../Common/Waveform.jsx';
import Footer from '../Common/Footer.jsx';
import useClipLookup from '../Common/useClipLookup.js';
import { useFontScale } from '../../utils/useFontScale.js';
import { Button, Card, Badge, Toggle } from '../Common/DesignComponents.jsx';
import '../../styles/theme.css';

export const GAME_MODES = [
  {
    id: 'soundoff',
    name: 'Sound Off',
    sub: 'Duel your pairs',
    badge: 'Anchor Mode',
    icon: '⚔️',
    description: 'Player-authored prompts answered in head-to-head duels with 2-clip pick hands.',
    minPlayers: 3,
  },
  {
    id: 'namethatnoise',
    name: 'Name That Noise',
    sub: 'Bluff the answer',
    badge: 'Bluffing',
    icon: '❓',
    description: 'Hear mystery sound, write bluffs, and vote to spot the real label.',
    minPlayers: 2,
  },
  {
    id: 'drift',
    name: 'Drift',
    sub: 'Pass the chain',
    badge: 'Telephone',
    icon: '🌀',
    description: 'Audio telephone chain mangling phrase → clip → phrase to grand reveal.',
    minPlayers: 3,
  },
  {
    id: 'foley',
    name: 'Foley Scene',
    sub: 'Build a scene',
    badge: 'Radio Drama',
    icon: '🎬',
    description: 'Build a 3-clip timed audio scene with custom gaps for player scenarios.',
    minPlayers: 3,
  },
];

export function Lobby({
  roomCode = '',
  players = [],
  soundEnabled = false,
  onEnableSound = () => {},
  onStartGame = () => {},
  onShowRules = null,
  onSelectMode = () => {},
  onUpdateSettings = () => {},
  onKickPlayer = () => {},
  onPlayClip = null,
  onRestartRoom = null,
  isDevOrTest = true,
}) {
  const lookupClip = useClipLookup();
  const { scale: fontScale, setScale: setFontScale } = useFontScale();
  const [selectedMode, setSelectedMode] = useState('soundoff');
  const [rounds, setRounds] = useState(3);
  const [timerSeconds, setTimerSeconds] = useState(60);
  const [contentRating, setContentRating] = useState('party');
  const [hostSpendCredits, setHostSpendCredits] = useState(true);
  const [customMysteryName, setCustomMysteryName] = useState('');

  const handleModeSelect = (modeId) => {
    setSelectedMode(modeId);
    onSelectMode(modeId);
    onUpdateSettings({
      mode: modeId,
      rounds,
      timerSeconds,
      contentRating,
      hostSpendCredits,
      mysteryClipName: customMysteryName,
    });
  };

  const handleSettingsChange = (newSettings) => {
    onUpdateSettings({
      mode: selectedMode,
      rounds,
      timerSeconds,
      contentRating,
      hostSpendCredits,
      mysteryClipName: customMysteryName,
      ...newSettings,
    });
  };

  const activeModeObj = GAME_MODES.find((m) => m.id === selectedMode) || GAME_MODES[0];
  const playerList = Array.isArray(players) ? players : Object.values(players || {}).filter(p => !p.isHost);
  const canStart = soundEnabled && (playerList.length >= activeModeObj.minPlayers || isDevOrTest);

  const displayCode = roomCode || '----';
  const codeChars = displayCode.split('');

  const totalSeatsCount = Math.max(6, playerList.length);
  const seats = [];
  for (let i = 0; i < totalSeatsCount; i++) {
    if (playerList[i]) {
      seats.push({ taken: true, player: playerList[i] });
    } else {
      seats.push({ taken: false });
    }
  }

  return (
    <div data-testid="host-lobby" className="sd-container" style={{ display: 'flex', flexDirection: 'column', gap: '28px' }}>
      {/* Sound Gate Banner */}
      {soundEnabled ? (
        <div
          data-testid="sound-gate-active"
          style={{
            background: 'rgba(143, 199, 160, 0.12)',
            border: '1px solid rgba(143, 199, 160, 0.35)',
            color: 'var(--sd-sage, #8FC7A0)',
            padding: '14px 22px',
            borderRadius: '14px',
            display: 'flex',
            alignItems: 'center',
            gap: '10px',
            fontWeight: 600,
            fontSize: '1rem',
          }}
        >
          <span style={{ fontSize: '1.2rem' }}>🔊</span> Audio System Ready & Enabled
        </div>
      ) : (
        <div
          className="sound-gate-banner"
          data-testid="sound-gate-banner"
          style={{
            background: 'var(--sd-amber, #E0A458)',
            color: '#08090B',
            padding: '16px 24px',
            borderRadius: '16px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: '16px',
            boxShadow: 'var(--sd-glow-amber, 0 0 24px rgba(224, 164, 88, 0.5))',
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <span style={{ fontSize: '1.8rem' }}>🔊</span>
            <div>
              <div style={{ fontWeight: 800, fontSize: '1.2rem', color: '#08090B' }}>
                Audio Required for Host Screen
              </div>
              <div style={{ fontSize: '0.95rem', color: '#1c1813', fontWeight: 600 }}>
                iOS/Safari & browsers require user gesture to enable Web Audio playback
              </div>
            </div>
          </div>
          <button
            type="button"
            className="sound-gate-button"
            data-testid="sound-gate-button"
            onClick={onEnableSound}
            style={{
              background: '#08090B',
              color: '#EDEFF2',
              border: '2px solid var(--sd-amber, #E0A458)',
              padding: '12px 24px',
              fontSize: '1.1rem',
              fontWeight: 700,
              borderRadius: '12px',
              cursor: 'pointer',
              whiteSpace: 'nowrap',
              minHeight: 'var(--sd-tap-cta, 56px)',
            }}
          >
            <span data-testid="enable-sound-btn">Tap to enable sound</span>
          </button>
        </div>
      )}

      {/* Hero 2-Column Section: Left (Room Code) & Right (Roster) */}
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'minmax(300px, 440px) minmax(0, 1fr)',
          gap: '24px',
          minHeight: '0',
        }}
      >
        {/* Left Column: Room Code & Join Instructions */}
        <div
          style={{
            border: '1px solid var(--sd-line, #1A1F25)',
            padding: '36px 32px',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            gap: '32px',
            background: 'var(--sd-panel, #0D1015)',
            borderRadius: '16px',
          }}
        >
          <div>
            <div
              style={{
                fontFamily: 'var(--sd-font-mono), "IBM Plex Mono", monospace',
                fontSize: '0.8rem',
                letterSpacing: '0.2em',
                textTransform: 'uppercase',
                color: 'var(--sd-dim, #6E7982)',
                marginBottom: '10px',
              }}
            >
              On your phone, go to
            </div>
            <div
              style={{
                fontSize: '2.15rem',
                fontWeight: 800,
                letterSpacing: '-0.028em',
                color: 'var(--sd-ink, #EDEFF2)',
              }}
            >
              memedrift.com
            </div>
          </div>

          <div>
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                marginBottom: '14px',
              }}
            >
              <div
                style={{
                  fontFamily: 'var(--sd-font-mono), "IBM Plex Mono", monospace',
                  fontSize: '0.8rem',
                  letterSpacing: '0.2em',
                  textTransform: 'uppercase',
                  color: 'var(--sd-dim, #6E7982)',
                }}
              >
                Room code
              </div>
              {onRestartRoom && (
                <button
                  type="button"
                  data-testid="lobby-restart-room-btn"
                  onClick={onRestartRoom}
                  style={{
                    background: 'none',
                    border: 'none',
                    color: 'var(--sd-dim, #6E7982)',
                    cursor: 'pointer',
                    fontSize: '0.75rem',
                    fontFamily: 'var(--sd-font-mono), "IBM Plex Mono", monospace',
                    letterSpacing: '0.1em',
                    textTransform: 'uppercase',
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '6px',
                    padding: '4px 8px',
                    borderRadius: '6px',
                    transition: 'color 120ms ease, background 120ms ease',
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.color = 'var(--sd-ink, #EDEFF2)';
                    e.currentTarget.style.background = 'var(--sd-panel-2, #0E1116)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.color = 'var(--sd-dim, #6E7982)';
                    e.currentTarget.style.background = 'none';
                  }}
                >
                  🔄 New Code
                </button>
              )}
            </div>
            <div
              data-testid="room-code-display"
              className="sd-font-mono"
              style={{
                fontFamily: '"IBM Plex Mono", var(--sd-font-mono), monospace',
                color: 'var(--sd-amber, #E0A458)',
                display: 'flex',
                gap: '12px',
                flexWrap: 'wrap',
              }}
            >
              {codeChars.map((ch, i) => (
                <div
                  key={i}
                  style={{
                    width: 'clamp(60px, 4.5rem, 80px)',
                    height: 'clamp(74px, 5.8rem, 100px)',
                    borderRadius: '16px',
                    border: '1px solid var(--sd-line-3, #1F252C)',
                    background: 'var(--sd-ground, #08090B)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <span
                    style={{
                      fontFamily: '"IBM Plex Mono", var(--sd-font-mono), monospace',
                      fontSize: '3.1rem',
                      fontWeight: 600,
                      lineHeight: 1,
                      color: 'var(--sd-amber, #E0A458)',
                    }}
                  >
                    {ch}
                  </span>
                </div>
              ))}
            </div>
          </div>

          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '11px',
              padding: '14px 18px',
              borderRadius: '12px',
              border: '1px solid var(--sd-line, #1A1F25)',
              background: 'rgba(26, 31, 37, 0.4)',
            }}
          >
            <div
              style={{
                width: '8px',
                height: '8px',
                borderRadius: '50%',
                background: 'var(--sd-amber, #E0A458)',
                flex: 'none',
              }}
            >
              <span
                style={{
                  display: 'inline-block',
                  width: '8px',
                  height: '8px',
                  borderRadius: '50%',
                  background: 'var(--sd-amber, #E0A458)',
                }}
              />
            </div>
            <span style={{ fontSize: '0.88rem', lineHeight: '1.45', color: 'var(--sd-muted, #79838D)' }}>
              This screen is the room speaker. Turn it up — phones stay quiet.
            </span>
          </div>
        </div>

        {/* Right Column: Player Roster */}
        <div
          style={{
            padding: '28px 32px',
            display: 'flex',
            flexDirection: 'column',
            minHeight: '0',
            background: 'var(--sd-panel, #0D1015)',
            borderRadius: '16px',
            border: '1px solid var(--sd-line, #1A1F25)',
          }}
        >
          <div
            style={{
              display: 'flex',
              alignItems: 'baseline',
              justifyContent: 'space-between',
              marginBottom: '20px',
            }}
          >
            <span style={{ fontSize: '21px', fontWeight: 700, letterSpacing: '-0.018em', color: 'var(--sd-ink, #EDEFF2)' }}>
              In the room
            </span>
            <span
              style={{
                fontFamily: 'var(--sd-font-mono), "IBM Plex Mono", monospace',
                fontSize: '13px',
                color: 'var(--sd-dim, #6E7982)',
                fontVariantNumeric: 'tabular-nums',
              }}
            >
              {playerList.length} of {totalSeatsCount} seats
            </span>
          </div>

          <div
            data-testid="player-roster"
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fill, minmax(170px, 1fr))',
              gap: '14px',
              alignContent: 'start',
            }}
          >
            {seats.map((seat, idx) => {
              if (seat.taken) {
                const p = seat.player;
                return (
                  <div
                    key={p.id || p.name || idx}
                    style={{
                      borderRadius: '14px',
                      border: '1px solid var(--sd-line-3, #1F252C)',
                      background: 'var(--sd-ground, #08090B)',
                      padding: '16px 18px',
                      display: 'flex',
                      flexDirection: 'column',
                      justifyContent: 'space-between',
                      gap: '12px',
                      position: 'relative',
                    }}
                  >
                    <SoundAvatar
                      clip={lookupClip(p.avatar, p.name)}
                      seed={p.name}
                      size="lg"
                      onClick={onPlayClip ? () => onPlayClip(lookupClip(p.avatar, p.name) || p.avatar) : undefined}
                      title={onPlayClip ? `Click to replay ${p.name}'s signature sound` : undefined}
                      style={{ width: '100%', height: '54px', cursor: onPlayClip ? 'pointer' : 'default' }}
                    />
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '8px' }}>
                      <span
                        style={{
                          fontSize: '16px',
                          fontWeight: 700,
                          letterSpacing: '-0.012em',
                          color: 'var(--sd-ink, #EDEFF2)',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                        }}
                      >
                        {p.name}
                      </span>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                        {onPlayClip && !p.isHost && (
                          <button
                            type="button"
                            title={`Replay ${p.name}'s sound`}
                            onClick={() => onPlayClip(lookupClip(p.avatar, p.name) || p.avatar)}
                            style={{
                              background: 'var(--sd-panel-2, #0E1116)',
                              border: '1px solid var(--sd-line-2, #1C2229)',
                              borderRadius: '6px',
                              color: 'var(--sd-teal, #3ACFCF)',
                              cursor: 'pointer',
                              fontSize: '0.75rem',
                              padding: '3px 7px',
                              fontFamily: "var(--sd-font-mono, 'IBM Plex Mono'), monospace",
                            }}
                          >
                            ▶
                          </button>
                        )}
                        {p.isHost && (
                          <Badge variant="amber" size="sm">
                            HOST
                          </Badge>
                        )}
                        {!p.isHost && (
                          <button
                            type="button"
                            title="Kick player"
                            onClick={() => onKickPlayer(p.id)}
                            style={{
                              background: 'transparent',
                              border: 'none',
                              color: 'var(--sd-rose, #E4738A)',
                              cursor: 'pointer',
                              fontSize: '0.85rem',
                              padding: '2px',
                            }}
                          >
                            ❌
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                );
              }

              return (
                <div
                  key={`empty-${idx}`}
                  style={{
                    borderRadius: '14px',
                    border: '1px dashed var(--sd-line-2, #1C2229)',
                    background: 'transparent',
                    padding: '16px 18px',
                    minHeight: '110px',
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'space-between',
                    gap: '12px',
                  }}
                >
                  <Waveform peaks={null} bars={44} height={54} />
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <span
                      style={{
                        fontFamily: 'var(--sd-font-mono), "IBM Plex Mono", monospace',
                        fontSize: '12px',
                        letterSpacing: '0.14em',
                        textTransform: 'uppercase',
                        color: 'var(--sd-dim-2, #5A646D)',
                      }}
                    >
                      Open seat
                    </span>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* Mode Selector */}
      <div style={{ marginTop: '8px' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '16px' }}>
          <h2 style={{ margin: 0, fontSize: '20px', color: 'var(--sd-ink, #EDEFF2)', fontWeight: 700 }}>
            Select Game Mode
          </h2>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            {onShowRules && (
              <button
                type="button"
                data-testid="how-to-play-btn"
                onClick={onShowRules}
                style={{
                  minHeight: '34px',
                  padding: '0 14px',
                  borderRadius: '9px',
                  border: '1px solid var(--sd-line-2, #1C2229)',
                  background: 'var(--sd-panel-2, #0E1116)',
                  color: 'var(--sd-ink-2, #C6CDD4)',
                  fontSize: '13.5px',
                  fontWeight: 600,
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px',
                  transition: 'all 0.15s ease',
                }}
              >
                <span>📖</span>
                <span>How to play</span>
              </button>
            )}
            <Badge variant="amber" size="md">
              Selected: {activeModeObj.name}
            </Badge>
          </div>
        </div>

        <div
          data-testid="mode-selector"
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
            gap: '14px',
          }}
        >
          {GAME_MODES.map((mode) => {
            const isSelected = selectedMode === mode.id;
            return (
              <div
                key={mode.id}
                data-testid={`mode-card-${mode.id}`}
                onClick={() => handleModeSelect(mode.id)}
                style={{
                  padding: '18px 22px',
                  borderRadius: '14px',
                  border: `1px solid ${isSelected ? 'var(--sd-amber, #E0A458)' : 'var(--sd-line-2, #1C2229)'}`,
                  background: isSelected ? 'rgba(224, 164, 88, 0.08)' : 'var(--sd-panel, #0D1015)',
                  cursor: 'pointer',
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '8px',
                  transition: 'all 0.15s ease',
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <span
                    style={{
                      fontSize: '16px',
                      fontWeight: 700,
                      letterSpacing: '-0.008em',
                      color: isSelected ? 'var(--sd-ink, #EDEFF2)' : 'var(--sd-muted, #8A939C)',
                    }}
                  >
                    {mode.name}
                  </span>
                  <Badge variant={isSelected ? 'amber' : 'outline'} size="sm">
                    Min: {mode.minPlayers}
                  </Badge>
                </div>
                <span
                  style={{
                    fontFamily: 'var(--sd-font-mono), "IBM Plex Mono", monospace',
                    fontSize: '11px',
                    letterSpacing: '0.06em',
                    color: isSelected ? 'var(--sd-amber, #E0A458)' : 'var(--sd-dim, #6E7982)',
                  }}
                >
                  {mode.sub}
                </span>
                <p style={{ margin: 0, fontSize: '0.85rem', color: 'var(--sd-dim, #6E7982)', lineHeight: 1.35 }}>
                  {mode.description}
                </p>
              </div>
            );
          })}
        </div>
      </div>

      {/* Settings & Action Controls Footer */}
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '24px', alignItems: 'center', justifyContent: 'space-between' }}>
        {/* Settings Panel */}
        <Card variant="default" data-testid="settings-panel" style={{ flex: '1 1 400px', background: 'var(--sd-panel, #0D1015)', border: '1px solid var(--sd-line, #1A1F25)' }}>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '20px', alignItems: 'center', justifyContent: 'space-between' }}>
            {/* Rounds */}
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
              <label style={{ fontSize: '0.9rem', color: 'var(--sd-ink-2, #C6CDD4)', fontWeight: 600 }}>Rounds:</label>
              <div style={{ display: 'flex', gap: '6px' }}>
                {[1, 2, 3, 4, 5].map((r) => (
                  <button
                    key={r}
                    type="button"
                    onClick={() => {
                      setRounds(r);
                      handleSettingsChange({ rounds: r });
                    }}
                    style={{
                      width: '32px',
                      height: '32px',
                      borderRadius: '8px',
                      border: rounds === r ? '2px solid var(--sd-amber, #E0A458)' : '1px solid var(--sd-line-2, #1C2229)',
                      background: rounds === r ? 'var(--sd-amber, #E0A458)' : 'var(--sd-ground, #08090B)',
                      color: rounds === r ? '#08090B' : 'var(--sd-ink, #EDEFF2)',
                      fontWeight: 700,
                      cursor: 'pointer',
                    }}
                  >
                    {r}
                  </button>
                ))}
              </div>
            </div>

            {/* Timer */}
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
              <label style={{ fontSize: '0.9rem', color: 'var(--sd-ink-2, #C6CDD4)', fontWeight: 600 }}>Timer:</label>
              <select
                aria-label="Timer / Phase"
                value={timerSeconds}
                onChange={(e) => {
                  const val = Number(e.target.value);
                  setTimerSeconds(val);
                  handleSettingsChange({ timerSeconds: val });
                }}
                style={{
                  background: 'var(--sd-ground, #08090B)',
                  color: 'var(--sd-ink, #EDEFF2)',
                  border: '1px solid var(--sd-line-2, #1C2229)',
                  borderRadius: '8px',
                  padding: '6px 10px',
                  fontSize: '0.9rem',
                  fontWeight: 600,
                }}
              >
                <option value={30}>30s</option>
                <option value={45}>45s</option>
                <option value={60}>60s (Default)</option>
                <option value={90}>90s</option>
              </select>
            </div>

            {/* Content Rating */}
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
              <label style={{ fontSize: '0.9rem', color: 'var(--sd-ink-2, #C6CDD4)', fontWeight: 600 }}>Rating:</label>
              <select
                aria-label="Content Rating"
                value={contentRating}
                onChange={(e) => {
                  const val = e.target.value;
                  setContentRating(val);
                  handleSettingsChange({ contentRating: val });
                }}
                style={{
                  background: 'var(--sd-ground, #08090B)',
                  color: 'var(--sd-ink, #EDEFF2)',
                  border: '1px solid var(--sd-line-2, #1C2229)',
                  borderRadius: '8px',
                  padding: '6px 10px',
                  fontSize: '0.9rem',
                  fontWeight: 600,
                }}
              >
                <option value="family">Family Friendly (PG)</option>
                <option value="party">Party Mode (PG-13)</option>
                <option value="unfiltered">Unfiltered (R)</option>
              </select>
            </div>

            {/* Text Size Scale */}
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
              <label style={{ fontSize: '0.9rem', color: 'var(--sd-ink-2, #C6CDD4)', fontWeight: 600 }}>Text Size:</label>
              <select
                aria-label="Text Size"
                data-testid="lobby-font-scale-select"
                value={fontScale}
                onChange={(e) => {
                  const val = Number(e.target.value);
                  setFontScale(val);
                }}
                style={{
                  background: 'var(--sd-ground, #08090B)',
                  color: 'var(--sd-ink, #EDEFF2)',
                  border: '1px solid var(--sd-line-2, #1C2229)',
                  borderRadius: '8px',
                  padding: '6px 10px',
                  fontSize: '0.9rem',
                  fontWeight: 600,
                }}
              >
                <option value={1.0}>Standard (100%)</option>
                <option value={1.2}>Large (120%)</option>
                <option value={1.35}>TV 10-Foot (135%)</option>
                <option value={1.5}>Extra Large (150%)</option>
              </select>
            </div>

            {/* Host Funds Toggle */}
            <Toggle
              label="Host Funds Room Audio"
              description=""
              checked={hostSpendCredits}
              onChange={(val) => {
                setHostSpendCredits(val);
                handleSettingsChange({ hostSpendCredits: val });
              }}
              icon="💳"
              style={{ background: 'transparent', border: 'none', padding: 0 }}
            />
          </div>

          {selectedMode === 'namethatnoise' && (
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px', width: '100%', marginTop: '12px', paddingTop: '12px', borderTop: '1px solid var(--sd-line-2, #1C2229)' }}>
              <label style={{ fontSize: '0.85rem', color: 'var(--sd-amber, #E0A458)', fontWeight: 700, whiteSpace: 'nowrap', fontFamily: "var(--sd-font-mono, 'IBM Plex Mono'), monospace", textTransform: 'uppercase', letterSpacing: '0.08em' }}>
                Mystery Answer:
              </label>
              <input
                type="text"
                data-testid="lobby-mystery-name-input"
                placeholder="Optional: Set custom correct answer name (leave blank for default)..."
                value={customMysteryName}
                onChange={(e) => {
                  const val = e.target.value;
                  setCustomMysteryName(val);
                  handleSettingsChange({ mysteryClipName: val });
                }}
                style={{
                  flex: 1,
                  background: 'var(--sd-ground, #08090B)',
                  color: 'var(--sd-ink, #EDEFF2)',
                  border: '1px solid var(--sd-line-2, #1C2229)',
                  borderRadius: '8px',
                  padding: '6px 12px',
                  fontSize: '0.85rem',
                  outline: 'none',
                }}
              />
            </div>
          )}
        </Card>

        {/* Start Game Button (Prominent TV CTA) */}
        <Button
          variant="amber"
          size="tv"
          data-testid="start-game-btn"
          disabled={!canStart}
          onClick={onStartGame}
          style={{
            minHeight: '68px',
            height: '68px',
            padding: '0 48px',
            borderRadius: '16px',
            fontSize: 'clamp(19px, 1.6vw, 24px)',
            fontWeight: 800,
            letterSpacing: '-0.015em',
            background: canStart ? 'var(--sd-amber, #E0A458)' : 'var(--sd-panel-2, #0E1116)',
            color: canStart ? '#08090B' : 'var(--sd-dim, #6E7982)',
            border: canStart ? '2px solid rgba(255, 255, 255, 0.2)' : '1px solid var(--sd-line-2, #1C2229)',
            boxShadow: canStart ? '0 0 28px rgba(224, 164, 88, 0.45), 0 8px 20px rgba(0, 0, 0, 0.6)' : 'none',
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '14px',
            cursor: canStart ? 'pointer' : 'not-allowed',
            transition: 'all 0.2s cubic-bezier(0.16, 1, 0.3, 1)',
          }}
        >
          <span>Start {activeModeObj.name}</span>
          <svg width="22" height="22" viewBox="0 0 16 16" fill="none" aria-hidden="true">
            <path d="M3 8h9.4M9 4.4L12.8 8 9 11.6" stroke={canStart ? '#08090B' : 'var(--sd-dim, #6E7982)'} strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
        </Button>
      </div>

      <Footer />
    </div>
  );
}

export default Lobby;
